import { defineStore } from 'pinia';

import { CookieEnum, ParamEnum } from '@interface/public';
import Config from '@constant/config';
import FN from '@constant/function';
import { completeCustomerLogin } from '@server/customerLogin';

export interface UserStateInterface { // 用户状态
  token: string; // 会话
  userID: string; // 用户标识
  username: string; // 用户名
  source: string; // 源地址
  sourceAccess: string; // 访问源地址
  operate: string; // 操作
  store: boolean; // 是否授权访问商城
}

const urlHref = location.href,
  urlHash = location.hash.split('#')[1],
  urlParam = {
    token: FN.Url.get(ParamEnum.Token, urlHref) || FN.Url.get(ParamEnum.Token, urlHash) || FN.Cookie.get(CookieEnum.Token) || '',
    userID: FN.Url.get(ParamEnum.UserID, urlHref) || FN.Url.get(ParamEnum.UserID, urlHash) || FN.Cookie.get(CookieEnum.UserID) || '',
    username: FN.Url.get(ParamEnum.UserName, urlHref) || FN.Url.get(ParamEnum.UserName, urlHash) || FN.Cookie.get(CookieEnum.UserName) || '',
    source: FN.Url.get(ParamEnum.Source, urlHref) || FN.Url.get(ParamEnum.Source, urlHash) || '',
    sourceAccess: FN.Url.get(ParamEnum.SourceAccess, urlHref) || FN.Url.get(ParamEnum.SourceAccess, urlHash) || '',
    operate: FN.Url.get(ParamEnum.Operate, urlHref) || FN.Url.get(ParamEnum.Operate, urlHash) || ''
  };

// SSO 回调的新凭证必须覆盖旧 Cookie，否则刷新后会恢复成过期会话。
(urlParam.token && urlParam.userID && urlParam.username) &&
FN.Cookie.set({
  [CookieEnum.Token]: urlParam.token,
  [CookieEnum.UserID]: urlParam.userID,
  [CookieEnum.UserName]: urlParam.username
}, Config.Cookie.time, '/', Config.Cookie.domain);
completeCustomerLogin(urlHref, { token: urlParam.token, userID: urlParam.userID, username: urlParam.username });
// 只清理 SSO 回传的凭证参数，保留 lang、page 等业务参数（静态新闻页没有路由恢复，语言参数需留存）。
history.replaceState(null, null, FN.Url.del([
  ParamEnum.Token, ParamEnum.UserID, ParamEnum.UserName,
  ParamEnum.Source, ParamEnum.SourceAccess, ParamEnum.Operate, 'dh_login'
], urlHref));

/**
 * 用户数据
 */
export default defineStore('user', {
  state: (): UserStateInterface => { // 状态
    return {
      token: urlParam.token || '',
      userID: urlParam.userID || '',
      username: urlParam.username || '',
      source: urlParam.source || '',
      sourceAccess: urlParam.sourceAccess || '',
      operate: urlParam.operate || '',
      store: false
    };
  },
  getters: { // 获取
  },
  actions: { // 方法
    /**
     * 过滤链接
     */
    async filterLink(): Promise<any> {
      // 用模块初始化时保存的地址，避免路由启动重定向后把首页误当作初始地址
      const initialURL = new URL(urlHref),
        hash = initialURL.hash.split('#')[1] || '',
        hashPath = hash.split('?')[0],
        base = $Application.Router.options.history.base.replace(/\/$/, ''),
        directPath = initialURL.pathname,
        normalizedPath = base && (directPath === base || directPath.startsWith(`${ base }/`))
          ? directPath.slice(base.length) || '/'
          : directPath,
        path = hashPath || normalizedPath,
        query = Object.assign(
          $FN.Url.all(urlHref),
          hash.includes('?') ? $FN.Url.all(hash.substring(hash.indexOf('?'))) : {}
        );

      await $Application.Router.replace({
        path,
        query: $Lodash.omit(query, [
          String([ $Enum.ParamEnum.Token ]),
          String([ $Enum.ParamEnum.UserID ]),
          String([ $Enum.ParamEnum.UserName ]),
          String([ $Enum.ParamEnum.Operate ]),
          'dh_login'
        ])
      });
    },
    
    /**
     * 更新信息
     * @param {*} data 数据
     */
    updateInfo(data: any): void {
      $FN.Object.synchronize(this, data);
      
      $FN.Cookie.set({
        [$Enum.CookieEnum.Token]: this.token,
        [$Enum.CookieEnum.UserID]: this.userID,
        [$Enum.CookieEnum.UserName]: this.username
      }, $Config.Cookie.time, '/', $Config.Cookie.domain);
    },
    
    /**
     * 清理信息
     */
    clearInfo(): void {
      $FN.Object.synchronize(this, {
        token: '',
        userID: '',
        username: '',
        store: false
      });
      
      $FN.Cookie.del([
        $Enum.CookieEnum.Token,
        $Enum.CookieEnum.UserID,
        $Enum.CookieEnum.UserName
      ], '/', $Config.Cookie.domain);
    },
    
    /**
     * 清理源
     */
    clearSource(): void {
      $FN.Object.synchronize(this, {
        source: '',
        sourceAccess: ''
      });
    },
    
    /**
     * 清理操作
     */
    clearOperate(): void {
      $FN.Object.synchronize(this, {
        operate: ''
      });
    }
  }
});
